import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Archive, Pencil, Plus, RotateCcw, X } from 'lucide-react';

import { ApiError, api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';

interface ProjectItem {
  id: string;
  name: string;
  color: string;
  isActive: boolean;
}

export function AdminProjects() {
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState<ProjectItem | 'new' | null>(null);

  const list = useQuery({
    queryKey: ['admin', 'projects'],
    queryFn: () => api<ProjectItem[]>('/admin/projects?includeInactive=true'),
  });

  const toggleActive = useMutation({
    mutationFn: (p: ProjectItem) =>
      api<ProjectItem>(`/admin/projects/${p.id}`, {
        method: 'PATCH',
        body: JSON.stringify({ isActive: !p.isActive }),
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin', 'projects'] }),
  });

  const rows = list.data ?? [];

  return (
    <div className="p-6 md:p-8">
      <div className="bg-card border border-border rounded-sm shadow-md overflow-hidden">
        <header className="px-7 py-5 border-b border-border flex items-center justify-between gap-4 flex-wrap">
          <div>
            <h2 className="text-lg font-semibold tracking-tightish">Proyectos</h2>
            <p className="text-[13px] text-muted-foreground mt-1">
              Catálogo de proyectos para imputar horas al fichar. Los proyectos archivados
              no aparecen en el desplegable, pero las horas históricas se conservan.
            </p>
          </div>
          <Button variant="primary" size="sm" onClick={() => setEditing('new')}>
            <Plus className="h-3.5 w-3.5" />
            Nuevo proyecto
          </Button>
        </header>

        {list.isLoading ? (
          <p className="px-7 py-8 text-sm text-muted-foreground">Cargando…</p>
        ) : list.error ? (
          <p role="alert" className="px-7 py-8 text-sm text-destructive">
            {list.error instanceof ApiError ? list.error.message : 'Error cargando proyectos'}
          </p>
        ) : rows.length === 0 ? (
          <p className="px-7 py-10 text-center text-sm text-muted-foreground">
            Aún no hay proyectos. Crea el primero para empezar a imputar horas.
          </p>
        ) : (
          <table className="w-full text-[14px]">
            <thead>
              <tr>
                <Th>Nombre</Th>
                <Th align="center">Color</Th>
                <Th align="center">Estado</Th>
                <Th align="right">Acciones</Th>
              </tr>
            </thead>
            <tbody>
              {rows.map((p) => (
                <tr
                  key={p.id}
                  className={cn(
                    'border-b border-border last:border-b-0',
                    !p.isActive && 'opacity-60',
                  )}
                >
                  <td className="px-5 py-3.5">{p.name}</td>
                  <td className="px-5 py-3.5 text-center">
                    <span
                      className="inline-block h-4 w-4 rounded-full align-middle"
                      style={{ backgroundColor: p.color }}
                      aria-label={p.color}
                    />
                  </td>
                  <td className="px-5 py-3.5 text-center">
                    <span
                      className={cn(
                        'inline-flex items-center font-mono text-[10px] font-medium uppercase border rounded-[3px] px-2 py-0.5',
                        p.isActive
                          ? 'bg-success/10 border-success/40 text-success'
                          : 'bg-muted border-border text-muted-foreground',
                      )}
                      style={{ letterSpacing: '0.06em' }}
                    >
                      {p.isActive ? 'Activo' : 'Archivado'}
                    </span>
                  </td>
                  <td className="px-5 py-3.5 text-right">
                    <div className="inline-flex items-center gap-1">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setEditing(p)}
                        aria-label="Editar"
                      >
                        <Pencil className="h-3.5 w-3.5" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => toggleActive.mutate(p)}
                        disabled={toggleActive.isPending}
                        aria-label={p.isActive ? 'Archivar' : 'Reactivar'}
                      >
                        {p.isActive ? (
                          <Archive className="h-3.5 w-3.5" />
                        ) : (
                          <RotateCcw className="h-3.5 w-3.5" />
                        )}
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {editing ? (
        <ProjectModal
          target={editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            queryClient.invalidateQueries({ queryKey: ['admin', 'projects'] });
          }}
        />
      ) : null}
    </div>
  );
}

function Th({
  children,
  align = 'left',
}: {
  children: React.ReactNode;
  align?: 'left' | 'center' | 'right';
}) {
  return (
    <th
      className={cn(
        'font-mono text-[12px] font-medium uppercase text-muted-foreground bg-paper border-b border-border px-5 py-3.5',
        align === 'right' && 'text-right',
        align === 'center' && 'text-center',
        align === 'left' && 'text-left',
      )}
      style={{ letterSpacing: '0.08em' }}
    >
      {children}
    </th>
  );
}

const PALETTE = [
  '#1B23B8',
  '#22c55e',
  '#ef4444',
  '#a855f7',
  '#f59e0b',
  '#06b6d4',
  '#ec4899',
  '#0ea5e9',
];

function ProjectModal({
  target,
  onClose,
  onSaved,
}: {
  target: ProjectItem | 'new';
  onClose: () => void;
  onSaved: () => void;
}) {
  const isNew = target === 'new';
  const initial = isNew ? null : (target as ProjectItem);

  const [name, setName] = useState(initial?.name ?? '');
  const [color, setColor] = useState(initial?.color ?? PALETTE[0]);

  useEffect(() => {
    if (!isNew) {
      setName(initial!.name);
      setColor(initial!.color);
    }
  }, [isNew, initial]);

  const save = useMutation({
    mutationFn: () => {
      if (isNew) {
        return api<ProjectItem>('/admin/projects', {
          method: 'POST',
          body: JSON.stringify({ name: name.trim(), color }),
        });
      }
      return api<ProjectItem>(`/admin/projects/${initial!.id}`, {
        method: 'PATCH',
        body: JSON.stringify({ name: name.trim(), color }),
      });
    },
    onSuccess: () => onSaved(),
  });

  const canSubmit = name.trim().length >= 1 && !save.isPending;
  const error = save.error
    ? save.error instanceof ApiError
      ? save.error.message
      : 'Error al guardar'
    : null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" role="dialog" aria-modal>
      <div
        aria-hidden
        onClick={() => !save.isPending && onClose()}
        className="absolute inset-0"
        style={{ background: 'rgba(10,14,31,0.4)', backdropFilter: 'blur(2px)' }}
      />
      <div className="relative w-full max-w-[480px] bg-card rounded-sm shadow-lg overflow-hidden">
        <header className="flex items-center justify-between px-6 py-4 border-b border-border">
          <h2 className="text-[15px] font-semibold tracking-tightish">
            {isNew ? 'Nuevo proyecto' : 'Editar proyecto'}
          </h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Cerrar"
            className="h-8 w-8 inline-flex items-center justify-center rounded-sm border border-line-strong bg-card text-foreground hover:border-foreground transition-colors"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </header>

        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (canSubmit) save.mutate();
          }}
        >
          <div className="px-6 py-5 flex flex-col gap-4">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="proj-name">
                Nombre <span className="text-destructive">*</span>
              </Label>
              <Input
                id="proj-name"
                required
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Web corporativa 2026"
                maxLength={120}
              />
            </div>

            <div className="flex flex-col gap-1.5">
              <Label>Color</Label>
              <div className="flex flex-wrap gap-2">
                {PALETTE.map((c) => (
                  <button
                    key={c}
                    type="button"
                    onClick={() => setColor(c)}
                    className={cn(
                      'h-7 w-7 rounded-full border-2 transition-transform',
                      color === c ? 'border-foreground scale-110' : 'border-transparent',
                    )}
                    style={{ backgroundColor: c }}
                    aria-label={`Color ${c}`}
                  />
                ))}
              </div>
            </div>

            {error ? (
              <p role="alert" className="text-sm text-destructive">
                {error}
              </p>
            ) : null}
          </div>

          <footer className="flex justify-end gap-2 px-6 py-3.5 border-t border-border bg-paper">
            <Button type="button" variant="ghost" size="sm" onClick={onClose}>
              Cancelar
            </Button>
            <Button type="submit" variant="primary" size="sm" disabled={!canSubmit}>
              {save.isPending ? 'Guardando…' : 'Guardar'}
            </Button>
          </footer>
        </form>
      </div>
    </div>
  );
}
